/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, Dialog, DialogFooter, DialogType } from '@fluentui/react';
import { Link } from '@fluentui/react/lib/Link';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { Observer, observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { CluIntentCreationModal } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentCreationModal';
import { CluTagUtterancesUiStore } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { CluEditEntityPane } from 'src/clu/appShell/cluTagUtterancesPage/components/CluEditEntityPane';
import { CluEntityDropdownContainer } from 'src/clu/appShell/cluTagUtterancesPage/components/CluEntityDropdownContainer';
import { CluMoveUtterancesModal } from 'src/clu/appShell/cluTagUtterancesPage/components/CluMoveUtterancesModal';
import { CluTagSidePaneContainer } from 'src/clu/appShell/cluTagUtterancesPage/components/CluTagSidePaneContainer';
import { CluUploadUtterancesModal } from 'src/clu/appShell/cluTagUtterancesPage/components/CluUploadUtterancesModal';
import { CluUtteranceCommandBar } from 'src/clu/appShell/cluTagUtterancesPage/components/CluUtteranceCommandBar';
import { CluUtteranceTable, UtteranceDto } from 'src/clu/appShell/cluTagUtterancesPage/components/CluUtteranceTable';
import { CluSavingIndicator } from 'src/clu/appShell/cluTagUtterancesPage/components/cluSavingIndicator';
import { conversationTrainingLearnMore, orchestrationTrainingLearnMore } from 'src/clu/cluExternalLinks';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { CluBreadcrumb } from 'src/clu/modules/components/CluBreadcrumb';
import { useCluPaginatedUtterances } from 'src/clu/modules/hooks/useCluPaginatedUtterances';
import { useCluUtteranceActions } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { applyEntityFilter, applyIntentFilter } from 'src/clu/modules/utils/filtersUtils';
import { transformUtteranceLabels } from 'src/clu/modules/utils/labelingUtils';
import { CluConnectedIntentsDto } from 'src/clu/modules/utils/orchestrationUtils';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useSelection } from 'src/core/hooks/useSelection';
import { logButtonClick, logButtonSuccessfulClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';
import { lineDataAttribute } from 'src/packages/react-text-annotator/lib/esm';
import { FiltersPillContainer } from 'src/verticals/utterancesFilters/components/FiltersPillContainer';
import { SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type CluPassedFilters = { intentsIdsFilters: string[]; entitiesIdsFilters?: string[] };

const messages = defineMessages({
    pageTitle: { id: 'clu.cluTagUtterancesPage.pageTitle', defaultMessage: 'Data labeling' },
    filtersApplied: { id: 'clu.cluTagUtterancesPage.filtersApplied', defaultMessage: 'Filtered by:' },
    testingSetTapHeader: { id: 'clu.cluTagUtterancesPage.testingSetTapHeader', defaultMessage: 'Testing set' },
    trainingSetTapHeader: { id: 'clu.cluTagUtterancesPage.trainingSetTapHeader', defaultMessage: 'Training set' },
    deletingUtterancesTitle: { id: 'clu.cluTagUtterancesPage.deletingUtterancesTitle', defaultMessage: 'Delete utterance(s)' },
    pageDescription: {
        id: 'clu.cluTagUtterancesPage.pageDescription',
        defaultMessage:
            "To add an utterance, first select an intent that your user intends to accomplish. Then provide an example of what they’d say in that situation and label the information to extract as entities. After labeling the utterances, you'll be ready to create a model with this data in"
    },
    pageDescription2: {
        id: 'clu.cluTagUtterancesPage.pageDescription2',
        defaultMessage:
            'For example, for an intent of Order Pizza, an utterance might be “I’d like to get a large pizza.” The word “large” might then be labeled as a Pizza Size entity.'
    },
    trainingLink: {
        id: 'clu.cluTagUtterancesPage.trainingLink',
        defaultMessage: 'Training.'
    },
    emptyTrainingUtterancesTitle: {
        id: 'clu.cluTagUtterancesPage.emptyTrainingUtterancesTitle',
        defaultMessage: 'No utterances for training yet'
    },
    emptyTrainingUtterancesDescription: {
        id: 'clu.cluTagUtterancesPage.emptyTrainingUtterancesDescription',
        defaultMessage: 'Add utterances by associating them to an intent here, or upload a file with several utterances in it.'
    },
    emptyTestingUtterancesTitle: {
        id: 'clu.cluTagUtterancesPage.emptyTestingUtterancesTitle',
        defaultMessage: 'No utterances for testing yet'
    },
    emptyTestingUtterancesDescription: {
        id: 'clu.cluTagUtterancesPage.emptyTestingUtterancesDescription',
        defaultMessage:
            'Add utterances by associating them to an intent here, or upload a file with several utterances in it. Then make sure to explicitly label all entities in each testing utterance.'
    },
    deletingUtterancesBody: {
        id: 'clu.cluTagUtterancesPage.deletingUtterancesBody',
        defaultMessage: 'Are you sure you want to delete the selected utterance(s)?'
    },
    testingSetTapDescription: {
        id: 'clu.cluTagUtterancesPage.testingSetTapDescription',
        defaultMessage:
            'We’ll use these utterances to test the performance of your conversation model after creating your model. A separate set of utterances are used to train your model.'
    },
    trainingSetTapDescription: {
        id: 'clu.cluTagUtterancesPage.trainingSetTapDescription',
        defaultMessage:
            'We’ll use these utterances to create your conversation model during training. A separate set of utterances can test the performance of your model.'
    },
    learnMoreLink: {
        id: 'clu.cluTagUtterancesPage.learnMoreLink',
        defaultMessage: 'Learn more about splitting data between training and testing sets.'
    },
    duplicateUtteranceError: {
        id: 'clu.cluUtteranceTable.duplicateUtteranceError',
        defaultMessage: 'Your utterance "{utteranceText}" is duplicated. No duplicate utterances are allowed.'
    },
    dismissEditUtterance: { id: 'clu.cluUtteranceTable.dismissEditUtterance', defaultMessage: 'Dismiss' },
    moveFailed: { id: 'clu.cluUtteranceTable.moveFailed', defaultMessage: 'Move failed' }
});

const { getClassName, styleDiv } = getStylistV2('CluTagUtterancesPage');
const ScrollablePaneWrapper = styleDiv('ScrollablePaneWrapper', { flex: 1, position: 'relative' });

const pageClassName = getClassName('CluTagUtterancesPage', { paddingLeft: 32 });
const breadcrumbStickClassName = getClassName('BreadCrumbSticky', { marginLeft: -8 });
const breadcrumbWrapperClassName = getClassName('BreadcrumbWrapper', { marginLeft: -8, marginBottom: '18px' });
const paletteWidthPercentage = '20%';

const utteranceStoreToSwaggerFieldMap = new Map<keyof CluUtteranceStore, keyof CluUtteranceSwagger>([
    ['labeledIntentName', 'intent'],
    ['language', 'language'],
    ['text', 'text']
]);

export const CluTagUtterancesPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const location = useLocation();

    const { selection, selectedItems } = useSelection<CluUtteranceStore>();
    const [isDeletingAll, setIsDeletingAll] = React.useState(false);
    const { rootStore, dispatcher, uiStore: globalUiStore } = useGlobalState();
    const { isLargeOrGreater, isXLargeOrGreater } = useResponsiveMode();

    const { canWrite } = mapUserRoleToUserActions(rootStore.serviceKey.userRole);

    const tableRef = React.createRef<HTMLDivElement>();

    const [pivotTabsDisplay, setPivotTabsDisplay] = React.useState('block');
    const [pageChangeTrigger, setPageChangeTrigger] = React.useState(getId());

    const [sortKey, setSortKey] = React.useState<SortKeyType<CluUtteranceStore>>();
    const paletteMinWidth = isXLargeOrGreater ? '325px' : '160px';
    const [showUtteranceTeachingBubble, setShowUtteranceTeachingBubble] = React.useState<boolean>(true);
    const [routeFilterInfo] = React.useState(location.state as { dataSetTab: 'Train' | 'Test' });
    const [duplicatedUtterances, setDuplicatedUtterances] = React.useState<CluUtteranceStore[]>([]);

    const {
        culture,
        jsonData,
        projectType,
        isMultiLingual,
        entityStoreHouse,
        intentStoreHouse,
        gptPredictionsStore,
        id: currentProjectId,
        name: currentProjectName
    } = rootStore.currentCluProjectStore;
    const entities = entityStoreHouse.items;
    const rawUtterances = jsonData.utterances;
    const generatedUtterances = gptPredictionsStore.rawUtterances;

    const { serviceName } = getPortalFromUrl();
    const uiStore = useLocalStore<CluTagUtterancesUiStore>(
        () => new CluTagUtterancesUiStore([...generatedUtterances, ...rawUtterances], projectType, routeFilterInfo?.dataSetTab)
    );
    const intents = intentStoreHouse.items.filter(i => uiStore.selectedDataset === 'Test' || !i.orchestrationInfo);
    const { filtersStore, labelingStore, modalsConfigState } = uiStore;
    const datasetRedistribution = uiStore.rawUtterancesJson.reduce(
        (acc, utterance) => acc + ((utterance.dataset || 'Train') === 'Train' ? 1 : -1),
        0
    );

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            selection.setAllSelected(false);
            uiStore.setModalsConfigState({
                isMoveUtterancesModalHidden: true,
                isDeleteUtterancesModalHidden: true,
                isUploadUtterancesModalHidden: true,
                createNewIntentModal: { isHidden: true, initialIntentName: '' }
            });
        }
    });

    const filteredUtterances = React.useMemo(
        () =>
            orderBy(
                substringSearchStrategy(
                    uiStore.rawUtterancesJson.filter(u => (u.dataset || 'Train') === uiStore.selectedDataset || u.dataset === 'Generated'),
                    uiStore.searchQuery,
                    null,
                    (utterance, q) => utterance?.text.toLocaleLowerCase().indexOf(q) !== -1
                ).filter(
                    utterance =>
                        applyEntityFilter(filtersStore.appliedEntitiesFilter, utterance) &&
                        applyIntentFilter(filtersStore.appliedIntentsFilter, utterance)
                ),
                [createKeyedStringNormalizer(utteranceStoreToSwaggerFieldMap.get(sortKey?.key))],
                [sortKey?.order]
            ),
        [
            sortKey,
            pageChangeTrigger,
            uiStore.searchQuery,
            datasetRedistribution,
            uiStore.selectedDataset,
            uiStore.rawUtterancesJson,
            filtersStore.appliedEntitiesFilter,
            filtersStore.appliedIntentsFilter
        ]
    );

    const { currentPage, items, pageCount, onPageChange } = useCluPaginatedUtterances({
        utterances: filteredUtterances,
        pageSize: LuisConstants.Clu.NumberOfDisplayedUtterancesPerPage
    });

    const utteranceLabels = transformUtteranceLabels(
        uiStore.displayedUtterancesStores.find(item => item.id === labelingStore.menuStore.currentUtteranceId),
        entities
    ).labels;

    const {
        addLabel,
        saveChanges,
        removeLabels,
        editUtterance,
        reassignIntent,
        reassignDataset,
        submitUtterance,
        deleteUtterances,
        rejectGeneratedUtterance,
        acceptGeneratedUtterance,
        acceptAllGeneratedUtterance,
        rejectAllGeneratedUtterance
    } = useCluUtteranceActions(uiStore);

    React.useEffect(() => () => dequeueTeachingBubble('CluNewUtterance'), []);

    React.useEffect(() => uiStore.setUtteranceToEditId(null), [selectedItems]);

    React.useEffect(() => uiStore.setRawUtterancesJson([...generatedUtterances, ...rawUtterances]), [rawUtterances]);

    React.useEffect(
        () => uiStore.setRawUtterancesJson([...generatedUtterances, ...uiStore.rawUtterancesJson.filter(u => u.dataset !== 'Generated')]),
        [generatedUtterances]
    );

    React.useEffect(() => uiStore.setDisplayedUtteranceStores(items), [currentPage, filteredUtterances]);

    React.useEffect(() => {
        if (
            canWrite &&
            tableRef.current &&
            showUtteranceTeachingBubble &&
            projectType !== 'Orchestration' &&
            !globalUiStore.teachingBubbles.CluNewUtterance &&
            (uiStore.savingState === 'dirty'
                ? globalUiStore.teachingBubbles.CluSaveChanges && !globalUiStore.skippedTeachingBubbles.CluSaveChanges
                : globalUiStore.teachingBubbles.CluFilterView && !globalUiStore.skippedTeachingBubbles.CluFilterView)
        ) {
            const firstUtterance = tableRef.current.querySelector(`[${lineDataAttribute}='true']`);
            if (firstUtterance) {
                createTeachingBubble(
                    'CluNewUtterance',
                    localize(cluTeachingBubbleMessages.newUtteranceBody),
                    localize(cluTeachingBubbleMessages.newUtteranceTitle),
                    firstUtterance,
                    true,
                    true
                );
            }
        }
    }, [canWrite, uiStore.savingState, tableRef, projectType, showUtteranceTeachingBubble, Object.values(globalUiStore.teachingBubbles)]);

    React.useEffect(() => {
        if (!intentStoreHouse.tracker.isCompleted || !entityStoreHouse.tracker.isCompleted) {
            return;
        }

        let activeIntents: string[] = [];
        let activeEntities: string[] = [];

        if (history.location.state) {
            activeIntents = history.location.state ? (history.location.state as CluPassedFilters).intentsIdsFilters : [];
            activeEntities = history.location.state ? (history.location.state as CluPassedFilters).entitiesIdsFilters : [];
            history.location.state = null;
        }

        filtersStore.initializeEntityFilterValues(entities, activeEntities);
        filtersStore.initializeIntentFilterValues(intents, activeIntents);
    }, [intentStoreHouse.tracker.isCompleted, entityStoreHouse.tracker.isCompleted, uiStore.selectedDataset]);

    React.useEffect(() => {
        if (isLargeOrGreater) {
            setPivotTabsDisplay('block');
        } else {
            setPivotTabsDisplay('grid');
        }
    }, [isLargeOrGreater]);

    React.useEffect(() => {
        if (isDeletingAll) {
            deleteUtterances(
                items.map(v => v.id),
                defaultOperationsTracker,
                uiStore.selectedDataset
            );
        }
        if (pageCount === 1 && items.length === 0) {
            setIsDeletingAll(false);
        }
    }, [isDeletingAll, items, pageCount]);

    const getEmptyPlaceholder = () => {
        switch (uiStore.selectedDataset) {
            case 'Test':
                return (
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 13 }}>
                        <LuisText variant="large" fontWeight={600}>
                            {localize(messages.emptyTestingUtterancesTitle)}
                        </LuisText>
                        <LuisText styles={{ root: { textAlign: 'center', maxWidth: '380px' } }}>
                            {localize(messages.emptyTestingUtterancesDescription)}
                        </LuisText>
                    </Stack>
                );
            case 'Train':
            default:
                return (
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 13 }}>
                        <LuisText variant="large" fontWeight={600}>
                            {localize(messages.emptyTrainingUtterancesTitle)}
                        </LuisText>
                        <LuisText styles={{ root: { textAlign: 'center', maxWidth: '380px' } }}>
                            {localize(messages.emptyTrainingUtterancesDescription)}
                        </LuisText>
                    </Stack>
                );
        }
    };

    const toggleFiltersPane = React.useCallback(() => {
        if (uiStore.activeSidePane !== 'filters') {
            uiStore.setActiveSidePane('filters');
        } else {
            uiStore.setActiveSidePane(projectType === 'Conversation' ? 'entityPalette' : 'orchestrationDistribution');
        }
    }, [uiStore]);

    const toggleUtterancesGenerationPane = React.useCallback(() => {
        logButtonClick(serviceName, 'DataLabeling', `SuggestUtterances`, currentProjectId);
        if (uiStore.activeSidePane !== 'utterancesGptGeneration') {
            uiStore.setActiveSidePane('utterancesGptGeneration');
        } else {
            uiStore.setActiveSidePane(projectType === 'Conversation' ? 'entityPalette' : 'orchestrationDistribution');
        }
    }, [uiStore]);

    const openTaggingPane = React.useCallback(
        () => uiStore.setActiveSidePane(projectType === 'Conversation' ? 'entityPalette' : 'orchestrationDistribution'),
        [uiStore]
    );

    const moveUtterancesToDataset = () => {
        const targetDataset = uiStore.selectedDataset === 'Train' ? 'Test' : 'Train';
        const duplicateUtterance: CluUtteranceStore[] = [];
        selectedItems.forEach(v => {
            if (
                rootStore.currentCluProjectStore.jsonData.utterances
                    .filter(utterance => utterance.dataset === targetDataset)
                    .find(utterance => utterance.text === v.text)
            ) {
                duplicateUtterance.push(v);
            }
        });

        setDuplicatedUtterances(duplicateUtterance);
        if (duplicateUtterance.length > 0) return;
        reassignDataset(
            selectedItems.map(s => s.id),
            targetDataset
        );
        logButtonClick(
            serviceName,
            'DataLabeling',
            `MoveTo${uiStore.selectedDataset === 'Train' ? 'Test' : 'Train'}Set`,
            uiStore.selectedDataset,
            currentProjectId
        );
        selection.setAllSelected(false);
    };

    const handleFiltersChange = React.useCallback(
        (change: SelectionChange) => {
            filtersStore.changeFilter(change);
            logButtonClick(serviceName, 'DataLabeling', change.changeType, uiStore.selectedDataset, currentProjectId);
        },
        [filtersStore]
    );

    const handleEntityCreation = React.useCallback((name: string) => dispatcher.dispatch('addCluEntity', { name, toastData: {} }), [
        dispatcher
    ]);

    const handleUploadFiles = React.useCallback(
        (files: File[]) =>
            dispatcher.dispatch('uploadCluUtterancesFromFile', {
                files,
                dataset: uiStore.selectedDataset,
                tracker: defaultOperationsTracker
            }),
        [dispatcher]
    );

    const handlePageChange = (pageNumber: number) => {
        onPageChange(pageNumber);
        setPageChangeTrigger(getId());
    };

    const handleDatasetChange = (dataset: DatasetType) => {
        uiStore.setSelectedDataset(dataset);
        selection.setAllSelected(false);
    };

    const handleDismissUploadUtterance = () => {
        defaultOperationsTracker.reset();
        uiStore.setModalsConfigState({ isUploadUtterancesModalHidden: true });
    };

    const handleIntentReassign = (destIntentId: string) => {
        reassignIntent(
            selectedItems.map(s => s.id),
            destIntentId
        );
        selection.setItems([], false);
        setPageChangeTrigger(getId());
        uiStore.setModalsConfigState({ isMoveUtterancesModalHidden: true });
    };

    const handleCreateNewIntent = (data: CluConnectedIntentsDto) => {
        logButtonSuccessfulClick(serviceName, 'DataLabeling', 'AddIntent', uiStore.selectedDataset, currentProjectId);
        uiStore.setModalsConfigState({ createNewIntentModal: { isHidden: true, initialIntentName: '' } });
        uiStore.setSelectedIntentId(data.intentName);
    };

    const handleGeneratedUtteranceAcceptance = (utteranceId: number) => {
        acceptGeneratedUtterance(uiStore.displayedUtterancesStores.find(u => u.id === utteranceId) as CluGptPredictedUtteranceStore);
        selection.setAllSelected(false);
    };

    const handleGeneratedUtteranceRejection = (utteranceId: number) => {
        rejectGeneratedUtterance(uiStore.displayedUtterancesStores.find(u => u.id === utteranceId) as CluGptPredictedUtteranceStore);
        selection.setAllSelected(false);
    };

    const handleAcceptAllGeneratedUtterance = () => {
        onPageChange(0);
        setPageChangeTrigger(getId());
        logButtonClick(serviceName, 'DataLabeling', `AcceptAllSuggestedUtterances`, currentProjectId);
        acceptAllGeneratedUtterance();
        selection.setAllSelected(false);
    };

    const handleRejectAllGeneratedUtterance = () => {
        onPageChange(0);
        setPageChangeTrigger(getId());
        logButtonClick(serviceName, 'DataLabeling', `RejectAllSuggestedUtterances`, currentProjectId);
        rejectAllGeneratedUtterance();
        selection.setAllSelected(false);
    };
    const focusOnTarget = async (target: string) => {
        await setTimeout(Promise.resolve, 50);
        const delBtn = document.querySelector(target);
        if (delBtn) {
            (delBtn as HTMLElement).focus();
        } else {
            (document.querySelector('.ms-Button--commandBar.ms-Button--hasMenu.ms-CommandBar-overflowButton') as HTMLElement).focus();
        }
    };

    return (
        <Page
            reactToZoomLevel={false}
            className={pageClassName}
            title={localize(messages.pageTitle)}
            pageName={localize(messages.pageTitle)}>
            <Stack grow horizontal styles={{ root: { position: 'relative' } }}>
                <ScrollablePaneWrapper>
                    <ScrollablePane styles={{ contentContainer: { overflowX: 'hidden' } }}>
                        <Stack grow tokens={{ childrenGap: 15, padding: '0 20px 0 0' }}>
                            <span className={breadcrumbWrapperClassName}>
                                <Sticky
                                    stickyBackgroundColor="white"
                                    stickyPosition={StickyPositionType.Header}
                                    stickyClassName={breadcrumbStickClassName}>
                                    <CluBreadcrumb serviceType={projectType} />
                                </Sticky>
                            </span>

                            <Stack tokens={{ childrenGap: 5 }}>
                                <Stack horizontal={isLargeOrGreater} verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                    <EditableTitle title={localize(messages.pageTitle)} />
                                    <CluSavingIndicator savingState={uiStore.savingState} currentProjectName={currentProjectName} />
                                </Stack>
                                <Stack tokens={{ childrenGap: 3 }}>
                                    <LuisText variant="medium">
                                        {localize(messages.pageDescription)}
                                        &nbsp;
                                        <Link onClick={() => history.push(routeTable.clu.trainingPage())}>
                                            {localize(messages.trainingLink)}
                                        </Link>
                                    </LuisText>
                                    <LuisText variant="medium">{localize(messages.pageDescription2)}</LuisText>
                                </Stack>
                            </Stack>

                            <Stack grow tokens={{ childrenGap: 20 }}>
                                <Pivot
                                    linkSize="large"
                                    selectedKey={uiStore.selectedDataset}
                                    styles={{ root: { display: pivotTabsDisplay } }}
                                    onLinkClick={link => handleDatasetChange(link.props.itemKey as DatasetType)}>
                                    <PivotItem itemKey="Train" headerText={localize(messages.trainingSetTapHeader)} />
                                    <PivotItem itemKey="Test" headerText={localize(messages.testingSetTapHeader)} />
                                </Pivot>

                                <Stack>
                                    <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
                                        <CluUtteranceCommandBar
                                            query={uiStore.searchQuery}
                                            onFilter={toggleFiltersPane}
                                            selectedItems={selectedItems}
                                            savingState={uiStore.savingState}
                                            currentProjectName={currentProjectName}
                                            onMoveToDataset={moveUtterancesToDataset}
                                            currentActiveDataset={uiStore.selectedDataset}
                                            onUtterancesGenerationWithGpt={toggleUtterancesGenerationPane}
                                            onAcceptAllGeneratedUtterances={handleAcceptAllGeneratedUtterance}
                                            onRejectAllGeneratedUtterances={handleRejectAllGeneratedUtterance}
                                            onSave={() => {
                                                saveChanges();
                                                logButtonClick(
                                                    serviceName,
                                                    'DataLabeling',
                                                    'SaveLabels',
                                                    uiStore.selectedDataset,
                                                    currentProjectId
                                                );
                                            }}
                                            onQueryChange={query => uiStore.setSearchQuery(query)}
                                            isFilterSelected={uiStore.activeSidePane === 'filters'}
                                            onEdit={() => {
                                                uiStore.setUtteranceToEditId(selectedItems[0].id);
                                                logButtonClick(
                                                    serviceName,
                                                    'DataLabeling',
                                                    'EditUtterances',
                                                    uiStore.selectedDataset,
                                                    currentProjectId
                                                );
                                            }}
                                            onUpload={() => {
                                                uiStore.setModalsConfigState({ isUploadUtterancesModalHidden: false });
                                                logButtonClick(
                                                    serviceName,
                                                    'DataLabeling',
                                                    'UploadUtterances',
                                                    uiStore.selectedDataset,
                                                    currentProjectId
                                                );
                                            }}
                                            onDelete={() => {
                                                uiStore.setModalsConfigState({ isDeleteUtterancesModalHidden: false });
                                                logButtonClick(
                                                    serviceName,
                                                    'DataLabeling',
                                                    'DeleteUtterances',
                                                    uiStore.selectedDataset,
                                                    currentProjectId
                                                );
                                            }}
                                            onMoveToIntent={() => {
                                                uiStore.setModalsConfigState({ isMoveUtterancesModalHidden: false });
                                                logButtonClick(
                                                    serviceName,
                                                    'DataLabeling',
                                                    'DifferentIntent',
                                                    uiStore.selectedDataset,
                                                    currentProjectId
                                                );
                                            }}
                                            utteranceCount={
                                                uiStore.displayedUtterancesStores.filter(u => u.dataset === uiStore.selectedDataset).length
                                            }
                                        />
                                    </Sticky>

                                    {filtersStore.areFilterItemsApplied && (
                                        <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5, padding: '20px 0' }}>
                                            <LuisText>{localize(messages.filtersApplied)}</LuisText>

                                            <FiltersPillContainer
                                                filters={filtersStore.appliedFilters}
                                                onRemoveFilter={handleFiltersChange}
                                            />
                                        </Stack>
                                    )}
                                </Stack>

                                <LuisText variant="smallPlus">
                                    {localize(
                                        uiStore.selectedDataset === 'Train'
                                            ? messages.trainingSetTapDescription
                                            : messages.testingSetTapDescription
                                    )}
                                    &nbsp;
                                    <ExternalLink
                                        href={
                                            projectType === 'Conversation' ? conversationTrainingLearnMore : orchestrationTrainingLearnMore
                                        }>
                                        {localize(messages.learnMoreLink)}
                                    </ExternalLink>
                                </LuisText>

                                <div ref={tableRef} style={{ minWidth: '60%' }}>
                                    <CluUtteranceTable
                                        sortKey={sortKey}
                                        intents={intents}
                                        entities={entities}
                                        addLabel={addLabel}
                                        selection={selection}
                                        defaultLanguage={culture}
                                        labelingStore={labelingStore}
                                        onRemoveLabels={removeLabels}
                                        shouldShowLanguages={isMultiLingual}
                                        currentDataset={uiStore.selectedDataset}
                                        emptyPlaceholder={getEmptyPlaceholder()}
                                        items={uiStore.displayedUtterancesStores}
                                        selectedEntityId={uiStore.selectedEntityId}
                                        utteranceToEditId={uiStore.utteranceToEditId}
                                        overallItemsCount={filteredUtterances.length}
                                        isLabelingDisabled={projectType === 'Orchestration'}
                                        rejectGeneratedUtterance={handleGeneratedUtteranceRejection}
                                        acceptGeneratedUtterance={handleGeneratedUtteranceAcceptance}
                                        onDismissEditUtterance={() => uiStore.setUtteranceToEditId(null)}
                                        onEditUtterance={utteranceId => uiStore.setUtteranceToEditId(utteranceId)}
                                        areItemsFiltered={Boolean(uiStore.searchQuery || filtersStore.appliedFilters.length)}
                                        onSubmitEditUtterance={updatedData => editUtterance(uiStore.utteranceToEditId, updatedData)}
                                        onDeleteUtterance={() => uiStore.setModalsConfigState({ isDeleteUtterancesModalHidden: false })}
                                        selectedIntentId={
                                            uiStore.selectedIntentId || (history.location.state as CluPassedFilters)?.intentsIdsFilters?.[0]
                                        }
                                        submitUtterance={(newUtteranceData: UtteranceDto) => {
                                            submitUtterance({ ...newUtteranceData, dataset: uiStore.selectedDataset })
                                                .then(() => setShowUtteranceTeachingBubble(true))
                                                .catch(() => setShowUtteranceTeachingBubble(false));
                                        }}
                                        languages={(projectType === 'Conversation'
                                            ? rootStore.cluConversationCultureStoreHouse
                                            : rootStore.cluOrchestrationCultureStoreHouse
                                        ).items.map(l => ({ code: l.code, name: l.name }))}
                                        onCreateNewIntent={q =>
                                            uiStore.setModalsConfigState({
                                                createNewIntentModal: { isHidden: false, initialIntentName: q }
                                            })
                                        }
                                        onSortColumn={column =>
                                            setSortKey({
                                                key: column.key as keyof CluUtteranceStore,
                                                order: column.isSortedDescending ? 'asc' : 'desc'
                                            })
                                        }
                                    />

                                    <TablePagination
                                        pageCount={pageCount}
                                        currentPage={currentPage}
                                        onPageChange={p => handlePageChange(p.selected)}
                                    />
                                </div>
                            </Stack>
                        </Stack>

                        <Observer>
                            {() => (
                                <CluEntityDropdownContainer
                                    entities={entities}
                                    utteranceLabels={utteranceLabels}
                                    onOpenEntityPane={openTaggingPane}
                                    menuStore={labelingStore.menuStore}
                                    tokenSelectionMetadata={labelingStore.tokenSelectionMetadata}
                                    isEntityPaneOpen={uiStore.activeSidePane === 'entityPalette'}
                                    actions={{
                                        onAddLabel: addLabel,
                                        onRemoveLabels: removeLabels,
                                        onCreateEntity: handleEntityCreation
                                    }}
                                    onSetTokenSelectionMetadata={React.useCallback(
                                        data => labelingStore.setTokenSelectionMetadata(data),
                                        []
                                    )}
                                />
                            )}
                        </Observer>
                    </ScrollablePane>
                </ScrollablePaneWrapper>

                <div style={{ width: paletteWidthPercentage, minWidth: paletteMinWidth }}>
                    <CluTagSidePaneContainer
                        utterances={rawUtterances}
                        filtersStore={filtersStore}
                        labelingStore={labelingStore}
                        onAddEntity={handleEntityCreation}
                        onFiltersChange={handleFiltersChange}
                        activeSidePane={uiStore.activeSidePane}
                        selectedEntityId={uiStore.selectedEntityId}
                        datasetType={uiStore.selectedDataset}
                        setSelectedEntityId={id => uiStore.setSelectedEntityId(id)}
                        onEditEntity={entityId => uiStore.setEntityDetailsBladeConfigState({ isOpen: true, entityId })}
                        onPaneClose={() =>
                            uiStore.setActiveSidePane(projectType === 'Conversation' ? 'entityPalette' : 'orchestrationDistribution')
                        }
                    />
                </div>
            </Stack>

            <CluEditEntityPane
                isOpen={uiStore.entityDetailsBladeConfigState.isOpen}
                entityId={uiStore.entityDetailsBladeConfigState.entityId}
                onDismiss={() => uiStore.setEntityDetailsBladeConfigState({ isOpen: false })}
            />

            <CluUploadUtterancesModal
                projectType={projectType}
                onDismiss={handleDismissUploadUtterance}
                disabled={defaultOperationsTracker.isRunning}
                onSubmit={dto => handleUploadFiles(dto.files)}
                errorMessage={defaultOperationsTracker.error?.message}
                hidden={modalsConfigState.isUploadUtterancesModalHidden}
            />

            <CluMoveUtterancesModal
                disabled={defaultOperationsTracker.isRunning}
                hidden={modalsConfigState.isMoveUtterancesModalHidden}
                onDismiss={() => uiStore.setModalsConfigState({ isMoveUtterancesModalHidden: true })}
                onSubmit={({ selectedIntentId: destIntentId }) => handleIntentReassign(destIntentId)}
            />

            <DeletePromptModal
                disabled={defaultOperationsTracker.isRunning}
                hidden={modalsConfigState.isDeleteUtterancesModalHidden}
                dialogContentProps={{ title: localize(messages.deletingUtterancesTitle) }}
                onDismiss={async () => {
                    uiStore.setModalsConfigState({ isDeleteUtterancesModalHidden: true });
                    await focusOnTarget('[data-automation-id="deleteUtteranceButton"]');
                }}
                onDelete={async () => {
                    selection.isAllSelected()
                        ? (onPageChange(0), setIsDeletingAll(true))
                        : deleteUtterances(
                              selectedItems.map(item => item.id),
                              defaultOperationsTracker,
                              uiStore.selectedDataset
                          );
                    await focusOnTarget('[data-automation-id="deleteUtteranceButton"]');
                }}>
                <LuisText>{localize(messages.deletingUtterancesBody)}</LuisText>
            </DeletePromptModal>

            <CluIntentCreationModal
                enableIntentLinking={false}
                onSubmit={handleCreateNewIntent}
                hidden={modalsConfigState.createNewIntentModal.isHidden}
                initialName={modalsConfigState.createNewIntentModal.initialIntentName}
                onDismiss={() => uiStore.setModalsConfigState({ createNewIntentModal: { isHidden: true, initialIntentName: '' } })}
            />
            <Dialog
                hidden={duplicatedUtterances.length === 0}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: localize(messages.moveFailed),
                    closeButtonAriaLabel: localize(messages.dismissEditUtterance),
                    subText: localize(messages.duplicateUtteranceError, { utteranceText: duplicatedUtterances[0]?.text })
                }}>
                <DialogFooter>
                    <DefaultButton onClick={() => setDuplicatedUtterances([])} text={localize(messages.dismissEditUtterance)} />
                </DialogFooter>
            </Dialog>
        </Page>
    );
});
